import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, Edit3, Save, X, Palette, Pencil, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TILE_LAYERS = [
  { name: 'Estándar', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attr: '&copy; OpenStreetMap' },
  { name: 'Claro', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attr: '&copy; CARTO' },
  { name: 'Gris', url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', attr: '&copy; CARTO' },
  { name: 'Oscuro', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attr: '&copy; CARTO' },
  { name: 'Satélite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attr: '&copy; Esri' },
  { name: 'Topo', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attr: '&copy; OpenTopoMap' },
];

// Color palettes per concession (agency_id)
const CONCESSION_COLORS = {
  'ENK': '#E53935', 'TXO': '#1E88E5', 'ET': '#43A047', 'EZK': '#FB8C00',
  'BLB': '#8E24AA', 'AVA': '#00ACC1',
};
const FALLBACK_COLORS = ['#D81B60','#5E35B1','#3949AB','#039BE5','#00897B','#7CB342','#C0CA33','#FDD835','#FFB300','#F4511E','#6D4C41','#546E7A'];

function getRouteColor(route, agencyMap, idx) {
  if (route.data.route_color) return `#${route.data.route_color}`;
  const aid = route.data.agency_id || agencyMap[route.data.route_id] || '';
  if (CONCESSION_COLORS[aid]) return CONCESSION_COLORS[aid];
  return FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}

const MapViewPage = () => {
  const { projectId } = useParams();
  const [stops, setStops] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const [routeStopsMap, setRouteStopsMap] = useState({});
  const [selectedStop, setSelectedStop] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState([40.4168, -3.7038]);
  const [showStops, setShowStops] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedStops, setEditedStops] = useState({});
  const [saving, setSaving] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [selectedRouteForShape, setSelectedRouteForShape] = useState(null);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [selectedRouteForColor, setSelectedRouteForColor] = useState(null);
  const [routeColor, setRouteColor] = useState('#2563EB');
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [editShapeMode, setEditShapeMode] = useState(false);
  const [selectedShapeForEdit, setSelectedShapeForEdit] = useState(null);
  const [editingShapePoints, setEditingShapePoints] = useState([]);
  // Line selection: set of selected route_ids (empty = none selected = show all)
  const [selectedLines, setSelectedLines] = useState(new Set());
  // Expanded concessions in sidebar
  const [expandedConcessions, setExpandedConcessions] = useState(new Set());

  useEffect(() => { fetchMapData(); }, [projectId]);

  const fetchMapData = async () => {
    setLoading(true);
    try {
      const [stopsRes, routesRes, agenciesRes] = await Promise.all([
        axios.get(`${API_URL}/projects/${projectId}/records/stops`),
        axios.get(`${API_URL}/projects/${projectId}/records/routes`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/projects/${projectId}/records/agency`).catch(() => ({ data: [] })),
      ]);
      setStops(stopsRes.data);
      setRoutes(routesRes.data);
      setAgencies(agenciesRes.data);
      if (stopsRes.data.length > 0) {
        const valid = stopsRes.data.filter(s => s.data.stop_lat && s.data.stop_lon);
        if (valid.length > 0) {
          const avgLat = valid.reduce((sum, s) => sum + parseFloat(s.data.stop_lat), 0) / valid.length;
          const avgLon = valid.reduce((sum, s) => sum + parseFloat(s.data.stop_lon), 0) / valid.length;
          setCenter([avgLat, avgLon]);
        }
      }
    } catch { toast.error('Error al cargar datos del mapa'); } finally { setLoading(false); }
    axios.get(`${API_URL}/projects/${projectId}/map/polylines`).then(res => setPolylines(res.data)).catch(() => {});
    axios.get(`${API_URL}/projects/${projectId}/route-stops-map`).then(res => setRouteStopsMap(res.data)).catch(() => {});
  };

  // Build color map: route_id -> color
  const routeColorMap = useMemo(() => {
    const map = {};
    routes.forEach((r, idx) => { map[r.data.route_id] = getRouteColor(r, {}, idx); });
    return map;
  }, [routes]);

  // Agency name map
  const agencyNameMap = useMemo(() => {
    const m = {};
    agencies.forEach(a => { m[a.data.agency_id] = a.data.agency_name || a.data.agency_id; });
    return m;
  }, [agencies]);

  // Routes grouped by concession (agency_id)
  const routesByConcession = useMemo(() => {
    const groups = {};
    routes.forEach(r => {
      const aid = r.data.agency_id || 'Sin concesión';
      if (!groups[aid]) groups[aid] = [];
      groups[aid].push(r);
    });
    return groups;
  }, [routes]);

  // Filtered stops
  const displayStops = useMemo(() => {
    let result = stops;
    if (selectedLines.size > 0) {
      const stopIds = new Set();
      selectedLines.forEach(rid => {
        (routeStopsMap[rid] || []).forEach(sid => stopIds.add(sid));
      });
      if (stopIds.size > 0) result = result.filter(s => stopIds.has(s.data.stop_id));
    }
    if (searchTerm) {
      result = result.filter(s =>
        s.data.stop_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.data.stop_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return result;
  }, [stops, selectedLines, routeStopsMap, searchTerm]);

  // Filtered polylines
  const visiblePolylines = useMemo(() => {
    if (selectedLines.size === 0) return polylines;
    return polylines.filter(p => selectedLines.has(p.route_id));
  }, [polylines, selectedLines]);

  // Subroutes for selected lines
  const subRoutesForSelected = useMemo(() => {
    if (selectedLines.size === 0) return {};
    const result = {};
    selectedLines.forEach(rid => {
      result[rid] = polylines.filter(p => p.route_id === rid);
    });
    return result;
  }, [selectedLines, polylines]);

  const toggleLine = (routeId) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId); else next.add(routeId);
      return next;
    });
  };

  const selectAllInConcession = (agencyId) => {
    const rids = (routesByConcession[agencyId] || []).map(r => r.data.route_id);
    setSelectedLines(prev => {
      const next = new Set(prev);
      const allSelected = rids.every(rid => next.has(rid));
      if (allSelected) { rids.forEach(rid => next.delete(rid)); } else { rids.forEach(rid => next.add(rid)); }
      return next;
    });
  };

  const toggleConcession = (aid) => {
    setExpandedConcessions(prev => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  };

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
    setCenter([parseFloat(stop.data.stop_lat), parseFloat(stop.data.stop_lon)]);
  };

  const handleMarkerDragEnd = (stop, event) => {
    const newPos = event.target.getLatLng();
    const updated = { ...stop, data: { ...stop.data, stop_lat: newPos.lat.toFixed(6), stop_lon: newPos.lng.toFixed(6) } };
    setEditedStops(prev => ({ ...prev, [stop.id]: updated }));
    setStops(prev => prev.map(s => s.id === stop.id ? updated : s));
    toast.info(`Parada "${stop.data.stop_name}" movida.`);
  };

  const handleSaveStopChanges = async () => {
    if (Object.keys(editedStops).length === 0) return;
    setSaving(true);
    let ok = 0;
    try {
      for (const [sid, stop] of Object.entries(editedStops)) {
        try { await axios.put(`${API_URL}/projects/${projectId}/records/${sid}`, { data: stop.data }); ok++; } catch {}
      }
      if (ok > 0) { toast.success(`${ok} parada(s) actualizada(s)`); setEditedStops({}); setEditMode(false); }
    } finally { setSaving(false); }
  };

  const handleDrawCreated = async () => {
    if (!selectedRouteForShape || drawingPoints.length < 2) return;
    const shapeId = `shape_${selectedRouteForShape.data.route_id}_${Date.now()}`;
    setSaving(true);
    try {
      for (let i = 0; i < drawingPoints.length; i++) {
        await axios.post(`${API_URL}/projects/${projectId}/records`, {
          file_type: 'shapes', data: { shape_id: shapeId, shape_pt_lat: drawingPoints[i].lat.toFixed(6), shape_pt_lon: drawingPoints[i].lng.toFixed(6), shape_pt_sequence: String(i + 1) }
        });
      }
      await axios.put(`${API_URL}/projects/${projectId}/records/${selectedRouteForShape.id}`, { data: { ...selectedRouteForShape.data, shape_id: shapeId } });
      toast.success('Trazado creado'); fetchMapData(); setDrawMode(false); setSelectedRouteForShape(null); setDrawingPoints([]);
    } catch { toast.error('Error al crear trazado'); } finally { setSaving(false); }
  };

  const handleColorChange = async () => {
    if (!selectedRouteForColor) return;
    try {
      await axios.put(`${API_URL}/projects/${projectId}/records/${selectedRouteForColor.id}`, {
        data: { ...selectedRouteForColor.data, route_color: routeColor.replace('#', '') }
      });
      toast.success('Color actualizado'); setColorDialogOpen(false); fetchMapData();
    } catch { toast.error('Error al actualizar color'); }
  };

  const handleEditShape = async (shapeId) => {
    try {
      const res = await axios.get(`${API_URL}/projects/${projectId}/records/shapes?limit=50000`);
      const pts = res.data.filter(s => s.data.shape_id === shapeId)
        .sort((a, b) => parseInt(a.data.shape_pt_sequence || 0) - parseInt(b.data.shape_pt_sequence || 0))
        .map(s => ({ id: s.id, lat: parseFloat(s.data.shape_pt_lat), lng: parseFloat(s.data.shape_pt_lon), sequence: s.data.shape_pt_sequence }));
      setEditingShapePoints(pts); setSelectedShapeForEdit(shapeId); setEditShapeMode(true);
    } catch { toast.error('Error al cargar shape'); }
  };

  const handleShapePointDragEnd = (idx, event) => {
    const p = event.target.getLatLng();
    const u = [...editingShapePoints]; u[idx] = { ...u[idx], lat: p.lat, lng: p.lng }; setEditingShapePoints(u);
  };

  const handleSaveShapeEdits = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < editingShapePoints.length; i++) {
        const pt = editingShapePoints[i];
        const data = { shape_id: selectedShapeForEdit, shape_pt_lat: pt.lat.toFixed(6), shape_pt_lon: pt.lng.toFixed(6), shape_pt_sequence: String(i + 1) };
        if (pt.id?.startsWith('new_')) await axios.post(`${API_URL}/projects/${projectId}/records`, { file_type: 'shapes', data });
        else await axios.put(`${API_URL}/projects/${projectId}/records/${pt.id}`, { data });
      }
      toast.success('Shape actualizado'); setEditShapeMode(false); setSelectedShapeForEdit(null); setEditingShapePoints([]); fetchMapData();
    } catch { toast.error('Error'); } finally { setSaving(false); }
  };

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r flex flex-col">
        <div className="p-3 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold mb-2" data-testid="map-title">Vista de Mapa</h2>
          <Tabs defaultValue="lines" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-8">
              <TabsTrigger value="lines" className="text-xs">Líneas</TabsTrigger>
              <TabsTrigger value="stops" className="text-xs">Paradas ({displayStops.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="lines" className="space-y-2 mt-2">
              {selectedLines.size > 0 && (
                <Button size="sm" variant="ghost" className="w-full text-xs h-7" onClick={() => setSelectedLines(new Set())}>
                  <X className="w-3 h-3 mr-1" /> Limpiar selección ({selectedLines.size})
                </Button>
              )}
              {/* Edit tools */}
              {!drawMode && !editShapeMode ? (
                <Button data-testid="enable-draw-mode-button" onClick={() => setDrawMode(true)} variant="outline" className="w-full" size="sm">
                  <Pencil className="w-3 h-3 mr-1" /> Dibujar Trazado
                </Button>
              ) : editShapeMode ? (
                <div className="flex gap-1">
                  <Button onClick={handleSaveShapeEdits} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700" size="sm">
                    <Save className="w-3 h-3 mr-1" /> Guardar
                  </Button>
                  <Button onClick={() => { setEditShapeMode(false); setEditingShapePoints([]); }} variant="outline" size="sm"><X className="w-3 h-3" /></Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <select className="w-full rounded-md border px-2 py-1 text-xs" value={selectedRouteForShape?.id || ''} onChange={(e) => setSelectedRouteForShape(routes.find(r => r.id === e.target.value))}>
                    <option value="">Seleccionar ruta</option>
                    {routes.map(r => (<option key={r.id} value={r.id}>{r.data.route_short_name || r.data.route_id}</option>))}
                  </select>
                  {selectedRouteForShape && drawingPoints.length > 0 && (
                    <div className="flex gap-1">
                      <Button onClick={handleDrawCreated} disabled={drawingPoints.length < 2} className="flex-1 bg-green-600" size="sm">Guardar ({drawingPoints.length}pts)</Button>
                      <Button onClick={() => setDrawingPoints([])} variant="outline" size="sm">Limpiar</Button>
                    </div>
                  )}
                  <Button onClick={() => { setDrawMode(false); setSelectedRouteForShape(null); setDrawingPoints([]); }} variant="ghost" size="sm" className="w-full"><X className="w-3 h-3 mr-1" /> Cancelar</Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="stops" className="space-y-2 mt-2">
              {!editMode ? (
                <Button data-testid="enable-edit-mode-button" onClick={() => setEditMode(true)} variant="outline" className="w-full" size="sm">
                  <Edit3 className="w-3 h-3 mr-1" /> Modo Edición
                </Button>
              ) : (
                <div className="space-y-1">
                  <p className="text-[10px] text-blue-600 font-medium">Arrastra paradas para mover ({Object.keys(editedStops).length} modificadas)</p>
                  <div className="flex gap-1">
                    <Button onClick={handleSaveStopChanges} disabled={saving || !Object.keys(editedStops).length} className="flex-1 bg-green-600" size="sm"><Save className="w-3 h-3 mr-1" /> Guardar</Button>
                    <Button onClick={() => { setEditMode(false); setEditedStops({}); fetchMapData(); }} variant="outline" size="sm"><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input data-testid="map-search-input" placeholder="Buscar paradas..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-7 h-7 text-xs" />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={showStops} onChange={(e) => setShowStops(e.target.checked)} className="rounded w-3 h-3" />
                Mostrar en mapa
              </label>
            </TabsContent>
          </Tabs>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto text-xs">
          {loading ? <p className="text-center text-slate-500 p-4">Cargando...</p> : (
            <>
              {/* Concessions + lines */}
              {Object.entries(routesByConcession).sort(([a],[b]) => a.localeCompare(b)).map(([aid, rts]) => {
                const expanded = expandedConcessions.has(aid);
                const concColor = CONCESSION_COLORS[aid] || '#6366f1';
                const allSelected = rts.every(r => selectedLines.has(r.data.route_id));
                const someSelected = rts.some(r => selectedLines.has(r.data.route_id));
                return (
                  <div key={aid}>
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-slate-50 border-b cursor-pointer hover:bg-slate-100" onClick={() => toggleConcession(aid)}>
                      {expanded ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: concColor }} />
                      <span className="font-semibold flex-1">{agencyNameMap[aid] || aid}</span>
                      <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={() => selectAllInConcession(aid)} className="rounded w-3 h-3" onClick={e => e.stopPropagation()} />
                      <span className="text-slate-400">{rts.length}</span>
                    </div>
                    {expanded && rts.map(r => {
                      const rid = r.data.route_id;
                      const active = selectedLines.has(rid);
                      const color = routeColorMap[rid] || concColor;
                      const subRoutes = subRoutesForSelected[rid] || [];
                      return (
                        <div key={r.id}>
                          <div className={`flex items-center gap-1.5 px-5 py-1 border-b border-slate-100 cursor-pointer hover:bg-blue-50 ${active ? 'bg-blue-50' : ''}`} onClick={() => toggleLine(rid)}>
                            <input type="checkbox" checked={active} onChange={() => toggleLine(rid)} className="rounded w-3 h-3" onClick={e => e.stopPropagation()} />
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className="flex-1 truncate">{r.data.route_short_name || r.data.route_long_name || rid}</span>
                            <button onClick={(e) => { e.stopPropagation(); setSelectedRouteForColor(r); setRouteColor(color); setColorDialogOpen(true); }} className="text-slate-300 hover:text-slate-600">
                              <Palette className="w-3 h-3" />
                            </button>
                          </div>
                          {/* Subroutes when selected */}
                          {active && subRoutes.length > 0 && subRoutes.map(sr => (
                            <div key={sr.shape_id} className="flex items-center gap-1.5 px-8 py-0.5 text-[10px] text-slate-500 border-b border-slate-50">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                              <span className="truncate">{sr.shape_id}</span>
                              <span className="text-slate-400">{sr.positions.length}pts</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* Stops list when in stops tab or filtered */}
              {displayStops.length > 0 && displayStops.length < 200 && displayStops.map(stop => (
                <div key={stop.id} data-testid={`stop-item-${stop.id}`}
                  className={`px-3 py-1 border-b border-slate-50 cursor-pointer hover:bg-blue-50 ${selectedStop?.id === stop.id ? 'bg-blue-100' : ''}`}
                  onClick={() => handleStopClick(stop)}>
                  <p className="font-medium truncate">{stop.data.stop_name}</p>
                  <p className="text-[10px] text-slate-400">{stop.data.stop_id}</p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative" data-testid="map-container">
        {!loading && (
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} className="z-0">
            <LayersControl position="topright">
              {TILE_LAYERS.map((layer, idx) => (
                <LayersControl.BaseLayer key={layer.name} checked={idx === 0} name={layer.name}>
                  <TileLayer attribution={layer.attr} url={layer.url} />
                </LayersControl.BaseLayer>
              ))}
            </LayersControl>

            <MapClickHandler drawMode={drawMode && selectedRouteForShape} onPointAdd={(latlng) => setDrawingPoints(prev => [...prev, latlng])} />

            {drawMode && drawingPoints.map((p, i) => (
              <CircleMarker key={`d-${i}`} center={[p.lat, p.lng]} radius={5} pathOptions={{ color: '#10B981', fillColor: '#10B981', fillOpacity: 1, weight: 2 }} />
            ))}
            {drawMode && drawingPoints.length > 1 && (
              <Polyline positions={drawingPoints.map(p => [p.lat, p.lng])} color="#10B981" weight={4} dashArray="10,10" />
            )}

            {/* Stops */}
            {showStops && displayStops.map(stop => {
              const lat = parseFloat(stop.data.stop_lat), lon = parseFloat(stop.data.stop_lon);
              if (isNaN(lat) || isNaN(lon)) return null;
              const isEdited = editedStops[stop.id];
              const isSel = selectedStop?.id === stop.id;

              if (editMode) {
                return (
                  <Marker key={stop.id} position={[lat, lon]} draggable={true}
                    eventHandlers={{ dragend: (e) => handleMarkerDragEnd(stop, e) }}
                    icon={L.divIcon({ className: '', iconSize: [12, 12], iconAnchor: [6, 6],
                      html: `<div style="width:12px;height:12px;border-radius:50%;border:2px solid ${isEdited ? '#ea580c' : '#6366f1'};background:${isEdited ? '#fed7aa' : 'white'};cursor:grab;"></div>` })}>
                    <Popup><p className="text-xs font-semibold">{stop.data.stop_name}</p></Popup>
                  </Marker>
                );
              }

              return (
                <CircleMarker key={stop.id} center={[lat, lon]} radius={isSel ? 6 : 3}
                  pathOptions={{ color: isSel ? '#1d4ed8' : '#6366f1', fillColor: isSel ? '#3b82f6' : '#818cf8', fillOpacity: isSel ? 1 : 0.7, weight: isSel ? 2 : 1 }}
                  eventHandlers={{ click: () => handleStopClick(stop) }}>
                  <Popup>
                    <div className="p-1">
                      <p className="text-xs font-semibold">{stop.data.stop_name}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{stop.data.stop_id}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* Polylines */}
            {!editShapeMode && visiblePolylines.map(pl => (
              pl.positions.length > 1 && (
                <Polyline key={pl.shape_id} positions={pl.positions}
                  color={pl.color ? `#${pl.color}` : (routeColorMap[pl.route_id] || '#2563EB')}
                  weight={4} opacity={0.7}
                  eventHandlers={{ click: () => { if (!drawMode) handleEditShape(pl.shape_id); } }}>
                  <Popup>
                    <p className="text-xs font-semibold">{pl.route_name || pl.route_id || 'Ruta'}</p>
                    <p className="text-[10px] text-slate-500">{pl.shape_id} ({pl.positions.length}pts)</p>
                  </Popup>
                </Polyline>
              )
            ))}

            {/* Shape editing */}
            {editShapeMode && editingShapePoints.length > 1 && (
              <>
                <Polyline positions={editingShapePoints.map(p => [p.lat, p.lng])} color="#3B82F6" weight={5} opacity={0.8} />
                {editingShapePoints.map((p, i) => (
                  <Marker key={`dv-${i}`} position={[p.lat, p.lng]} draggable={true}
                    eventHandlers={{ dragend: (e) => handleShapePointDragEnd(i, e) }}
                    icon={L.divIcon({ className: '', iconSize: [16, 16], iconAnchor: [8, 8],
                      html: '<div style="width:16px;height:16px;border-radius:50%;border:3px solid #1d4ed8;background:white;cursor:grab;"></div>' })} />
                ))}
                {editingShapePoints.slice(0, -1).map((p, i) => {
                  const n = editingShapePoints[i + 1];
                  return (
                    <CircleMarker key={`m-${i}`} center={[(p.lat+n.lat)/2, (p.lng+n.lng)/2]} radius={4}
                      pathOptions={{ color: '#94a3b8', fillColor: '#e2e8f0', fillOpacity: 0.8, weight: 2 }}
                      eventHandlers={{ click: () => {
                        const pts = [...editingShapePoints];
                        pts.splice(i+1, 0, { id: `new_${Date.now()}`, lat: (p.lat+n.lat)/2, lng: (p.lng+n.lng)/2, sequence: '' });
                        setEditingShapePoints(pts.map((x,j) => ({ ...x, sequence: String(j+1) })));
                      }}} />
                  );
                })}
              </>
            )}
          </MapContainer>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2.5 z-[1000] text-[10px] space-y-1">
          <p className="font-semibold text-xs mb-1">Leyenda</p>
          {Object.entries(CONCESSION_COLORS).map(([aid, color]) => {
            const name = agencyNameMap[aid] || aid;
            const count = (routesByConcession[aid] || []).length;
            if (!count) return null;
            return (
              <div key={aid} className="flex items-center gap-1.5">
                <div className="w-3 h-1 rounded" style={{ backgroundColor: color }} />
                <span>{name} ({count})</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5 pt-1 border-t">
            <div className="w-2.5 h-2.5 bg-indigo-400 rounded-full" />
            <span>Paradas ({displayStops.length})</span>
          </div>
          {selectedLines.size > 0 && (
            <p className="text-blue-600 font-medium">{selectedLines.size} línea(s) seleccionada(s)</p>
          )}
        </div>
      </div>

      {/* Color Dialog */}
      <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Color de Ruta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Label>Ruta: {selectedRouteForColor?.data.route_short_name || selectedRouteForColor?.data.route_id}</Label>
            <div className="flex gap-2 items-center">
              <input data-testid="route-color-picker" type="color" value={routeColor} onChange={(e) => setRouteColor(e.target.value)} className="w-16 h-8 rounded border cursor-pointer" />
              <Input data-testid="route-color-input" value={routeColor} onChange={(e) => setRouteColor(e.target.value)} className="flex-1" />
              <div data-testid="route-color-preview" className="w-8 h-8 rounded-lg border-2" style={{ backgroundColor: routeColor }} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {['#E53935','#D81B60','#8E24AA','#5E35B1','#3949AB','#1E88E5','#039BE5','#00ACC1','#00897B','#43A047','#7CB342','#FDD835','#FFB300','#FB8C00','#F4511E','#6D4C41','#757575','#546E7A','#000000'].map(c => (
                <button key={c} className={`w-6 h-6 rounded-full border-2 hover:scale-110 ${routeColor === c ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setRouteColor(c)} />
              ))}
            </div>
            <div className="flex gap-2">
              <Button data-testid="save-color-button" onClick={handleColorChange} className="flex-1">Guardar</Button>
              <Button variant="outline" onClick={() => setColorDialogOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MapClickHandler = ({ drawMode, onPointAdd }) => {
  useMapEvents({ click: (e) => { if (drawMode) onPointAdd(e.latlng); } });
  return null;
};

export default MapViewPage;
